import type { BillingHistoryEvent } from "../../shared/billing-schemas";

/**
 * Billing history as sentences people say (ADR-0087): "Joe sent a quote to the customer", not
 * "quote sent". What an employee or the customer did names them; what Stripe reported stands on
 * its own. Anything not listed falls back to its event name in words.
 */

const DONE_BY_SOMEONE: Record<string, string> = {
  "ops.billing.quote.created": "drafted a quote",
  "ops.billing.quote.updated": "edited a quote",
  "ops.billing.quote.sent": "sent a quote to the customer",
  "ops.billing.quote.superseded": "replaced a quote",
  "ops.billing.quote.canceled": "canceled a quote",
  "ops.billing.quote.email_failed": "sent a quote, but its email did not go out",
  "ops.billing.quote.email_not_configured": "sent a quote without an email",
  "workspace.billing.quote.accepted": "accepted the quote",
  "workspace.billing.quote.declined": "declined the quote",
  "workspace.billing.quote.terms_approved": "accepted the quote, which set the terms",
  "workspace.billing.quote.terms_superseded": "accepted a quote that replaced the terms",
  "ops.billing.terms.created": "drafted terms",
  "ops.billing.terms.updated": "edited draft terms",
  "ops.billing.terms.approved": "approved terms",
  "ops.billing.terms.superseded": "replaced the terms",
  "ops.billing.terms.ended": "ended the terms",
  "ops.billing.terms.canceled": "canceled the terms",
  "ops.billing.customer.linked": "linked the customer to Stripe",
  "ops.billing.customer.email_changed": "changed the billing email",
  "ops.billing.invoice.issued": "sent an invoice through Stripe",
  "ops.billing.invoice.reissued": "retried sending an invoice",
  "ops.billing.invoice.recorded": "recorded an invoice sent outside Stripe",
  "ops.billing.invoice.paid_recorded": "recorded a payment",
  "ops.billing.invoice.paid_outside_stripe": "marked an invoice paid outside Stripe",
  "ops.billing.invoice.status_changed": "changed an invoice's status",
  "ops.billing.invoice.voided": "voided an invoice",
  "ops.billing.invoice.written_off": "wrote off an invoice",
  "ops.billing.subscription.created": "started a subscription",
  "ops.billing.subscription.recreated": "retried starting a subscription",
  "ops.billing.subscription.recorded": "recorded a subscription billed outside Stripe",
  "ops.billing.subscription.canceled": "canceled a subscription",
  "ops.billing.subscription.renewal_moved": "moved the next invoice date",
  "ops.billing.credit.granted": "applied a credit",
  "ops.billing.refund.created": "issued a refund",
  "ops.billing.reconciled": "checked everything against Stripe",
};

const REPORTED_BY_STRIPE: Record<string, string> = {
  "billing.invoice.paid": "An invoice was paid",
  "billing.invoice.past_due": "An invoice became past due",
  "billing.invoice.processing": "A bank payment started settling",
  "billing.invoice.updated": "An invoice changed in Stripe",
  "billing.refund.updated": "A refund changed in Stripe",
  "billing.subscription.canceled": "A subscription ended",
  "billing.subscription.past_due": "A subscription became past due",
  "billing.subscription.updated": "A subscription changed in Stripe",
  "billing.stripe.request_failed": "A request to Stripe failed",
};

function inWords(event: string): string {
  const words = event.replace(/^(ops\.|workspace\.)?billing\./, "").replace(/[._]/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** One history entry as a sentence. */
export function activitySentence(event: BillingHistoryEvent): string {
  const done = DONE_BY_SOMEONE[event.event];
  if (done) return `${event.actor?.displayName ?? "Someone"} ${done}`;
  const reported = REPORTED_BY_STRIPE[event.event];
  if (reported) return reported;
  const who = event.actor?.displayName ?? "Stripe";
  return `${inWords(event.event)} · ${who}`;
}
